import { rem } from 'polished'
import React from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import {
  CRUISE_SEARCH_HERO_IMAGE_ID_DESKTOP,
  CRUISE_SEARCH_HERO_IMAGE_ID_MOBILE,
  CRUISE_SEARCH_HERO_VIDEO_URL,
} from 'constants/cruise'
import { useCruiseSearchEnabled } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import CruiseSearchFiltersBar from '../SearchPage/Filters/CruiseSearchFiltersBar'
import CruisesTabContent from 'components/SearchV2/Components/Mobile/TabContents/CruisesTabContent'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'

const HeroContainer = styled(HeroImage)`
  min-height: unset;
  padding: ${rem(40)} 0;
  padding-bottom: ${rem(96)};
  margin-bottom: ${rem(24)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
    margin-bottom: 0;
  }
`

const CruisesVideoContainer = styled.div`
  height: ${rem(350)};
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(400)};
  }
`

const CruisesVideoBanner = styled.video`
  position: absolute;
  height: 100%;
  width: 100%;
  object-fit: cover;
`

const CruiseSearchContainerPaneContent = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  &.mobile-expanded {
    margin-top: ${rem(16)};
    margin-bottom: ${rem(16)};
  }
`

const CruiseSearchBarFloating = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  width: 100%;
  z-index: 2;

  &.floating-search-bar {
    position: relative;
    transform: unset;
    margin-top: ${rem(32)};
  }
`

interface Props {
  floatingSearchBar?: boolean
}

function CruisePageSearchHero({ floatingSearchBar }: Props) {
  const isCruiseSearchEnabled = useCruiseSearchEnabled()
  const isMobileDevice = useIsMobileDevice()
  const heroImageId = isMobileDevice ? CRUISE_SEARCH_HERO_IMAGE_ID_MOBILE : CRUISE_SEARCH_HERO_IMAGE_ID_DESKTOP

  return <>
    {isCruiseSearchEnabled && <HeroContainer
      gravity="auto"
      aria-hidden="true"
      brightness={-25}
      id={heroImageId}
      tabletAspectRatio="16:3"
    >
      <LayoutContainer relative>
        <Group direction="vertical" horizontalAlign="start" tabletHorizontalAlign="center" gap={8}>
          <Heading colour="neutral-eight" variant="heading2" className="hero-heading center">
            <i>Cruise</i> into your next escape
          </Heading>
          <BodyText variant="large" colour="neutral-eight" className="center">
            Choose from our cruises tailored to your taste.
          </BodyText>
        </Group>
      </LayoutContainer>

      <CruiseSearchBarFloating className={clsx({ 'floating-search-bar': floatingSearchBar })}>
        <LayoutContainer>
          <CSSBreakpoint min="tablet">
            <CruiseSearchContainerPaneContent>
              <CruiseSearchFiltersBar />
            </CruiseSearchContainerPaneContent>
          </CSSBreakpoint>

          <CSSBreakpoint max="mobile">
            <CruiseSearchContainerPaneContent className="mobile-expanded">
              <CruisesTabContent disableAutoFocus />
            </CruiseSearchContainerPaneContent>
          </CSSBreakpoint>
        </LayoutContainer>
      </CruiseSearchBarFloating>
    </HeroContainer>}

    {!isCruiseSearchEnabled && <CruisesVideoContainer>
      <CruisesVideoBanner
        controls={false}
        autoPlay
        playsInline
        loop
        muted
      >
        <source src={CRUISE_SEARCH_HERO_VIDEO_URL} type="video/mp4" />
      </CruisesVideoBanner>
      <Group direction="vertical" verticalAlign="center" fullHeight>
        <LayoutContainer relative>
          <Group direction="vertical" gap={24}>
            <Heading variant="heading1" colour="neutral-eight">
              Cruises
            </Heading>
            <Heading variant="heading3alt" colour="neutral-eight">
              Explore the world's best cruises
            </Heading>
            <TextButton kind="secondary" variant="ghost" href="#listings">
              Browse all offers
            </TextButton>
          </Group>
        </LayoutContainer>
      </Group>
    </CruisesVideoContainer>}
  </>
}

export default CruisePageSearchHero
